import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import { getValueGenerationFunction } from '@utils/evaluateValueGenerationFunction';
import { DEFAULT_PRECISION } from '@utils/index_ts';

const filterDataPointsWithValueByEngineeringId = (
  dataPoints: DataPointWithValue[],
  engineeringId: string,
  pointNames: string[],
): DataPointWithValue[] => {
  const filteredDataPointsWithPointNames = dataPoints.filter(
    (dataPoint) =>
      dataPoint.sourceDeviceId === engineeringId &&
      pointNames.includes(dataPoint.pointName),
  );

  return filteredDataPointsWithPointNames;
};

const createDataPointsMap = (
  dataPoints: DataPointWithValue[],
): Map<string | object, DataPointWithValue> => {
  const map = new Map<string | object, DataPointWithValue>();
  dataPoints.forEach((dataPoint) => {
    map.set(dataPoint.fullTopic, dataPoint);
  });
  return map;
};

const calculateDataPointValue = (
  dataPoint: DataPoint,
  findDataPointByAlias: (pointAlias: string) => DataPointWithValue | undefined,
): { value: string; timestamp: string } | null => {
  const { valueGenerationFunction } = dataPoint;

  if (!valueGenerationFunction) {
    return null;
  }

  const { function: funcName, argumentArray } = valueGenerationFunction;
  const calculateFunction = getValueGenerationFunction(funcName);

  if (!calculateFunction) {
    return null;
  }

  // Get the values of the argument data points
  const argumentValues = argumentArray.map((pointAlias) => {
    const sourceDataPoint = findDataPointByAlias(pointAlias);
    return sourceDataPoint?.value;
  });

  // Check if all required values are available
  if (argumentValues.some((value) => value === undefined)) {
    return null;
  }

  // Calculate the value
  const precision = dataPoint.decimalPrecision ?? DEFAULT_PRECISION;
  const calculatedValue = calculateFunction(
    argumentValues as unknown[],
  )?.toFixed(precision);

  if (calculatedValue === undefined) {
    return null;
  }

  return {
    value: calculatedValue,
    timestamp: new Date().toISOString(),
  };
};

export {
  filterDataPointsWithValueByEngineeringId,
  createDataPointsMap,
  calculateDataPointValue,
};
